import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 14: Practical Protocols
 * 40 cylinder stones arranged in spiral path
 * Optimized: reduced ground subdivisions, fewer point lights,
 * pre-cached ground distances, reusable Vector3, pre-created line geometries
 */
export default function SteppingStones({ audioData, isActive, position = [0, 0, 0] }) {
  const stonesRef = useRef([])
  const groundRef = useRef()
  const progressRef = useRef(0)

  // Reusable Vector3 for scale lerp (avoids per-frame allocation)
  const _unitScale = useMemo(() => new THREE.Vector3(1, 1, 1), [])

  // Reusable Color objects (avoids per-frame allocation)
  const _earthColor = useMemo(() => new THREE.Color('#8B7355'), [])
  const _goldColor = useMemo(() => new THREE.Color('#FFD700'), [])
  const _tempColor = useMemo(() => new THREE.Color(), [])

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  const stoneCount = 40

  // Create spiral path for stones
  const stonePath = useMemo(() => {
    const path = []
    const turns = 3
    const maxRadius = 4

    for (let i = 0; i < stoneCount; i++) {
      const t = i / (stoneCount - 1)
      const angle = t * turns * Math.PI * 2
      const radius = t * maxRadius

      path.push({
        x: Math.cos(angle) * radius,
        z: Math.sin(angle) * radius,
        y: 0.15,
        angle: angle
      })
    }

    return path
  }, [])

  // Reduced ground geometry: 20x20 = 400 vertices (was 50x50 = 2,500)
  const groundGeometry = useMemo(() => {
    return new THREE.PlaneGeometry(10, 10, 20, 20)
  }, [])

  // Pre-cache ground vertex distances (computed once)
  const groundDistances = useMemo(() => {
    const positions = groundGeometry.attributes.position.array
    const count = groundGeometry.attributes.position.count
    const distances = new Float32Array(count)

    for (let i = 0; i < count; i++) {
      const x = positions[i * 3]
      const z = positions[i * 3 + 1] // In PlaneGeometry before rotation, y is the second axis
      distances[i] = Math.sqrt(x * x + z * z)
    }

    return distances
  }, [groundGeometry])

  // Pre-create path line geometries (not per render)
  const pathLineGeometries = useMemo(() => {
    return stonePath.slice(1).map((stone, i) => {
      const prev = stonePath[i]
      const points = [
        new THREE.Vector3(prev.x, 0.05, prev.z),
        new THREE.Vector3(stone.x, 0.05, stone.z)
      ]
      return new THREE.BufferGeometry().setFromPoints(points)
    })
  }, [stonePath])

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    // Progress based on audio
    if (amplitude > 0.1) {
      progressRef.current += delta * 0.3 * (1 + amplitude)
    } else {
      progressRef.current += delta * 0.1
    }

    if (beat > 0.5) {
      progressRef.current += 0.5
    }

    if (progressRef.current > stoneCount) {
      progressRef.current = 0
    }

    const currentStoneIndex = Math.floor(progressRef.current)

    // Animate stones
    stonesRef.current.forEach((stoneGroup, i) => {
      if (!stoneGroup) return

      const stoneProgress = i / (stoneCount - 1)
      const stoneIsActive = i <= progressRef.current
      const isCurrent = currentStoneIndex === i

      const mesh = stoneGroup.children[0]
      if (!mesh || !mesh.material) return

      // Height animation
      let height = 0.15
      if (isCurrent) {
        height = 0.15 + Math.sin(time * 1.5) * 0.1 + 0.2
      } else if (stoneIsActive) {
        height = 0.2
      }
      stoneGroup.position.y = height

      // Scale
      if (isCurrent) {
        stoneGroup.scale.set(1, 1.2 + Math.sin(time * 1.5) * 0.1, 1)
      } else {
        stoneGroup.scale.lerp(_unitScale, delta * 5)
      }

      // Color transition using reusable Color objects
      _tempColor.copy(_earthColor).lerp(_goldColor, stoneIsActive ? stoneProgress : 0)
      mesh.material.color.copy(_tempColor)
      mesh.material.emissive.copy(_tempColor)

      // Emissive intensity
      if (isCurrent) {
        mesh.material.emissiveIntensity = 1.5 + Math.sin(time * 1.5) * 0.5
      } else if (stoneIsActive) {
        mesh.material.emissiveIntensity = 0.4
      } else {
        mesh.material.emissiveIntensity = 0.1
      }

      if (isCurrent) {
        stoneGroup.rotation.y += delta * 0.5
      }
    })

    // Ground gentle wave (reduced vertex count)
    if (groundRef.current) {
      const vertices = groundRef.current.geometry.attributes.position.array
      const count = groundRef.current.geometry.attributes.position.count

      for (let i = 0; i < count; i++) {
        const wave = Math.sin(groundDistances[i] * 2 - time * 0.8) * 0.05
        vertices[i * 3 + 2] = wave // z-axis for rotated plane
      }

      groundRef.current.geometry.attributes.position.needsUpdate = true
    }
  })

  return (
    <group position={position}>
      {/* Ground plane */}
      <mesh
        ref={groundRef}
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0, 0]}
        geometry={groundGeometry}
      >
        <meshStandardMaterial
          color="#2F4F2F"
          roughness={0.9}
          metalness={0.1}
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Stepping stones - no per-stone point lights (massive perf savings) */}
      {stonePath.map((stone, i) => (
        <group
          key={`stone-${i}`}
          position={[stone.x, stone.y, stone.z]}
          ref={(el) => (stonesRef.current[i] = el)}
        >
          <mesh>
            <cylinderGeometry args={[0.25, 0.3, 0.15, 16]} />
            <meshStandardMaterial
              color="#8B7355"
              emissive="#8B7355"
              emissiveIntensity={0.1}
              roughness={0.8}
              metalness={0.2}
            />
          </mesh>
        </group>
      ))}

      {/* Path connecting lines - pre-created geometries */}
      {pathLineGeometries.map((geom, i) => (
        <line key={`path-${i}`} geometry={geom}>
          <lineBasicMaterial
            color="#8B7355"
            transparent
            opacity={0.3}
            linewidth={1}
          />
        </line>
      ))}

      {/* Ambient lighting - 2 lights instead of 40 */}
      <ambientLight intensity={0.3} />
      <pointLight position={[0, 5, 0]} intensity={1} color="#FFD700" distance={15} />
    </group>
  )
}
